// reverseGeocode.js — resolves lat/lon pairs to human-readable place names
// using the Nominatim OpenStreetMap reverse-geocoding API (no API key required).
//
// Usage policy: Nominatim requires a valid User-Agent header and enforces a 1 req/s
// rate limit per IP (https://operations.osmfoundation.org/policies/nominatim/).
// `geocodeUnits` serialises requests with ≥1 s gaps to stay within policy.
//
// Error/timeout policy: any failure returns `null` — geocoding is always best-effort.
// The caller omits the field rather than blocking generation.

const NOMINATIM_URL = 'https://nominatim.openstreetmap.org/reverse';
const USER_AGENT = 'dash-ui-v1/sitrep (+https://dash.minsky.ai)';
const TIMEOUT_MS = 3000;
// Nominatim rate-limit: 1 req/s absolute maximum. Use 1100 ms gap for safety.
const RATE_LIMIT_MS = 1100;

/**
 * Reverse-geocode `(lat, lon)` to a human-readable location context object.
 *
 * zoom=14 returns suburb/village-level detail (more precise than zoom=10 city level).
 *
 * @param {number} lat
 * @param {number} lon
 * @returns {Promise<{country: string|null, state: string|null, county: string|null, city: string|null, displayName: string|null}|null>}
 */
export async function reverseGeocode(lat, lon) {
    if (typeof lat !== 'number' || typeof lon !== 'number') return null;
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;

    const url = `${NOMINATIM_URL}?format=json&lat=${lat}&lon=${lon}&zoom=14&addressdetails=1`;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);

    try {
        const response = await fetch(url, {
            signal: controller.signal,
            headers: {'User-Agent': USER_AGENT},
        });
        if (!response.ok) return null;

        const data = await response.json();
        const addr = data?.address ?? {};

        return {
            country: addr.country ?? null,
            state: addr.state ?? null,
            county: addr.county ?? addr.district ?? addr.region ?? null,
            city: addr.city ?? addr.town ?? addr.village ?? addr.municipality ?? null,
            displayName: data?.display_name ?? null,
        };
    } catch (_) {
        return null;
    } finally {
        clearTimeout(timeoutId);
    }
}

/**
 * Format a geocode result into a compact, analyst-friendly location string.
 * E.g. "Sloviansk, Donetsk Oblast, Ukraine".
 *
 * @param {{city?: string|null, state?: string|null, country?: string|null, displayName?: string|null}|null} geocode
 * @returns {string|null}
 */
export function formatLocationName(geocode) {
    if (!geocode) return null;
    const parts = [geocode.city, geocode.state, geocode.country].filter(Boolean);
    if (parts.length > 0) return parts.join(', ');
    return geocode.displayName ?? null;
}

/**
 * Resolve a human-readable `locationName` for each unit that has lat/lon coordinates,
 * respecting Nominatim's 1 req/s rate limit by serialising requests with a
 * {@link RATE_LIMIT_MS} gap between them.
 *
 * Only the first `maxUnits` geocodable units are processed to bound total latency
 * (default 6 ≈ 6.6 s max, which falls inside the sitrep's existing "queued" wait).
 *
 * @param {Array<{ref: string, lat?: number|null, lon?: number|null}>} units
 * @param {{maxUnits?: number}} [opts]
 * @returns {Promise<Map<string, string>>} Map from unit `ref` → location name string.
 */
export async function geocodeUnits(units, {maxUnits = 6} = {}) {
    const result = new Map();
    if (!Array.isArray(units)) return result;

    const geocodable = units
        .filter(u => typeof u.lat === 'number' && typeof u.lon === 'number'
            && Number.isFinite(u.lat) && Number.isFinite(u.lon))
        .slice(0, maxUnits);

    for (let i = 0; i < geocodable.length; i++) {
        if (i > 0) await new Promise((r) => setTimeout(r, RATE_LIMIT_MS));
        const {ref, lat, lon} = geocodable[i];
        const geocode = await reverseGeocode(lat, lon);
        const name = formatLocationName(geocode);
        if (name) result.set(ref, name);
    }

    return result;
}

/**
 * Computes the centre coordinate of a bbox (`[[minLon, minLat], [maxLon, maxLat]]`) as
 * `{lat, lon}`, or `null` when the bbox is null/malformed.
 *
 * @param {[[number, number], [number, number]]|null} bbox
 * @returns {{lat: number, lon: number}|null}
 */
export function bboxCenter(bbox) {
    if (!Array.isArray(bbox) || bbox.length < 2) return null;
    const [sw, ne] = bbox;
    if (!Array.isArray(sw) || !Array.isArray(ne)) return null;
    const lon = (sw[0] + ne[0]) / 2;
    const lat = (sw[1] + ne[1]) / 2;
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
    return {lat, lon};
}
